import { movePageCommand } from '../router/page-commands'
import { changePagePermission } from '../db/page-permissions'
import { queryOptions } from '@w/react-query/db'
import { useId, useState } from 'react'
import { useMutation, useQuery } from '@w/react-query'
import type { PageRef } from '@w/content-blocks/schema'
import { Button } from '@w/ui/components/button'
import { Input } from '@w/ui/components/input'
import { Dialog, DialogPopup, DialogHeader, DialogTitle, DialogPanel } from '@w/ui/components/dialog'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import type { SpaceResource } from '../router/spaces-resources'

/** `resource` comes from the published spaces view the page renders; absent while the workspace file is unavailable. */
export function PageSharing({ source, workspaceId, resource, open, onOpenChange }: {
  source: PageRef; workspaceId: string; resource: SpaceResource | undefined; open: boolean; onOpenChange: (open: boolean) => void
}) {
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogPopup data-testid="flashcards-share-dialog">
      <DialogHeader><DialogTitle>Share page</DialogTitle></DialogHeader>
      <DialogPanel>{open ? resource ? <SharingControls workspaceId={workspaceId} pageId={source.pageId} db={resource.db} /> : <p>Sharing unavailable</p> : null}</DialogPanel>
    </DialogPopup>
  </Dialog>
}

// Render reads only the published view's db: the live registry (spaces.get) runs ahead of it and throws once the file closed.
function SharingControls({ workspaceId, pageId, db }: { workspaceId: string; pageId: string; db: SpaceResource['db'] }) {
  const ctx = useRouterContext()
  const controlId = useId()
  const snapshot = useQuery(ctx.rpc.page.levels.queryOptions({ workspaceId }), ctx.queryClient)
  const placements = useQuery(queryOptions(db.query`SELECT ${db.pageRelations.fromPageId}, ${db.pageRelations.toPageId} FROM ${db.pageRelations} WHERE ${db.pageRelations.type.eq('child')}`,
    { fromPageId: db.pageRelations.fromPageId, toPageId: db.pageRelations.toPageId }), ctx.queryClient)
  const grants = useQuery(queryOptions(db.query`SELECT ${db.pagePermissions.all} FROM ${db.pagePermissions}
    WHERE ${db.pagePermissions.pageId.eq(pageId)} ORDER BY ${db.pagePermissions.principalKind}, ${db.pagePermissions.principalId}`, db.pagePermissions.__selectSchema), ctx.queryClient)
  const [principalId, setPrincipalId] = useState('')
  const [level, setLevel] = useState<1 | 2 | 3>(1)
  const [parentPageId, setParentPageId] = useState('')
  const [teamspaceId, setTeamspaceId] = useState('')
  const command = useMutation({
    mutationFn: (action: 'grant' | 'restrict' | 'restore' | 'move') => Promise.resolve((async () => {
      const base = { workspaceId, pageId }
      if (action === 'grant') await changePagePermission(ctx, { ...base, type: 'grant', principalId: principalId.trim(), level })
      else if (action === 'move') {
        await movePageCommand(ctx, { source: { spaceId: workspaceId, pageId }, destination: { spaceId: workspaceId, parentId: parentPageId.trim() || (teamspaceId.trim() ? teamspaceId.trim() : null) } })
        await ctx.afterDirectoryCommand()
        await ctx.queryClient.invalidateQueries({ queryKey: ctx.rpc.page.levels.queryOptions({ workspaceId }).queryKey })
        await ctx.queryClient.invalidateQueries({ queryKey: ctx.rpc.page.sharing.queryOptions({ workspaceId, pageId }).queryKey })
      }
      else await changePagePermission(ctx, { ...base, type: 'inheritance', inheritance: action === 'restrict' ? 'restricted' : 'inherit' })
    })()),
  })
  // Revoke has its own pending state: the grants list updates while a grant is still settling, and its Revoke buttons must already work.
  const revoke = useMutation({
    mutationFn: (target: { principalKind: 'user'; principalId: string }) => Promise.resolve(changePagePermission(ctx, { workspaceId, pageId, type: 'revoke', principalId: target.principalId })),
  })
  // Locally created pages inherit the published ancestor capability until the server publishes their own level.
  let permissionPageId: string | undefined = pageId
  const visited = new Set<string>()
  while (permissionPageId && snapshot.data?.levels[permissionPageId] === undefined && !visited.has(permissionPageId)) {
    visited.add(permissionPageId)
    const current: string = permissionPageId
    permissionPageId = placements.data?.find(row => row.toPageId === current)?.fromPageId
  }
  const full = permissionPageId !== undefined && snapshot.data?.levels[permissionPageId] === 3
  if (!full) return <p>Sharing unavailable</p>
  return <fieldset className="flex flex-col gap-4">
    {revoke.error ? <p role="alert">Unable to change page permissions: {revoke.error.message}</p> : null}
    {grants.error ? <p role="alert">Sharing unavailable</p> : null}
    <ul className="flex flex-col gap-2">{grants.data?.map(grant => {
      const { principalKind: grantKind, principalId: grantId, level: grantLevel } = grant
      if (grantKind === null || grantId === null || grantLevel === null) return null
      return <li key={`${grantKind}:${grantId}`} className="flex items-center gap-2">
      <span>{grantKind}: {grantId} — {['', 'View', 'Edit', 'Full'][grantLevel]}</span>
      <Button data-testid={`page-share-revoke-${grantId}`} disabled={revoke.isPending} onClick={() => { revoke.mutate({ principalKind: grantKind, principalId: grantId }) }}>Revoke</Button>
    </li>
    })}</ul>
    <label htmlFor={`${controlId}-recipient`} className="flex flex-col gap-2">Recipient ID<Input id={`${controlId}-recipient`} data-testid="page-share-recipient" value={principalId} onChange={event => { setPrincipalId(event.target.value) }} /></label>
    <label className="flex flex-col gap-2">Access
      <select aria-label="Access" data-testid="page-share-access" value={level} onChange={event => { setLevel(event.target.value === '3' ? 3 : event.target.value === '2' ? 2 : 1) }}>
        <option value="1">View</option><option value="2">Edit</option><option value="3">Full</option>
      </select>
    </label>
    <Button disabled={!principalId.trim()} onClick={() => { command.mutate('grant') }}>Add grant</Button>
    <div className="flex flex-wrap gap-2">
      <Button disabled={command.isPending} onClick={() => { command.mutate('restrict') }}>Restrict inheritance</Button>
      <Button disabled={command.isPending} onClick={() => { command.mutate('restore') }}>Restore inheritance</Button>
    </div>
    <label htmlFor={`${controlId}-parent`} className="flex flex-col gap-2">Destination parent ID<Input id={`${controlId}-parent`} data-testid="page-move-parent" value={parentPageId} onChange={event => { setParentPageId(event.target.value) }} /></label>
    <label htmlFor={`${controlId}-teamspace`} className="flex flex-col gap-2">Destination teamspace ID<Input id={`${controlId}-teamspace`} data-testid="page-move-teamspace" value={teamspaceId} onChange={event => { setTeamspaceId(event.target.value) }} /></label>
    <Button disabled={command.isPending} onClick={() => { command.mutate('move') }}>Move page</Button>
    {command.error ? <p role="alert">Unable to change page permissions: {command.error.message}</p> : null}
  </fieldset>
}
